import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ChatService } from './chat.service';
import { IFileAttachment } from '../interfaces/streaming-messages.interface';
import { ALLOWED_FILE_EXTENSIONS, ALLOWED_EXTENSIONS_DISPLAY } from '../constants/file.constants';


export interface FileSelectionResult {
  addedFiles: IFileAttachment[];
  filesAdded: number;
  errors: { type: 'validation' | 'duplicate' | 'copy' | 'unknown'; fileName: string; message: string }[];
}

export interface LoadThreadFilesResult {
  files: IFileAttachment[];
  error?: string;
}

export interface RemoveFileResult {
  success: boolean;
  fileName: string;
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  private readonly ALLOWED_EXTENSIONS = ALLOWED_FILE_EXTENSIONS;

  constructor(private chatService: ChatService) {}

  /**
   * Check if Electron file selection is available
   */
  isElectronAvailable(): boolean {
    return !!(window as any).psse?.selectFiles;
  }

  /**
   * Select and validate files via Electron, copy to workspace
   */
  async selectAndCopyFiles(existingFiles: IFileAttachment[], threadId?: string): Promise<FileSelectionResult> {
    const result: FileSelectionResult = {
      addedFiles: [],
      filesAdded: 0,
      errors: []
    };

    if (!this.isElectronAvailable()) {
      result.errors.push({
        type: 'unknown',
        fileName: '',
        message: 'File selection is only available in Electron app'
      });
      return result;
    }

    try {
      const selectionResult = await (window as any).psse.selectFiles();

      if (!selectionResult.success || !selectionResult.files || selectionResult.files.length === 0) {
        return result;
      }

      for (const fileData of selectionResult.files) {
        await this.processSelectedFile(fileData, existingFiles, result, threadId);
      }
    } catch (error) {
      console.error('Error selecting files:', error);
      result.errors.push({
        type: 'unknown',
        fileName: '',
        message: 'Failed to select files'
      });
    }

    return result;
  }

  /**
   * Process a single selected file: validate, check duplicates, copy to workspace
   */
  private async processSelectedFile(
    fileData: any, 
    existingFiles: IFileAttachment[], 
    result: FileSelectionResult,
    threadId?: string
  ): Promise<void> {
    // Validate extension
    if (!fileData.type || !this.ALLOWED_EXTENSIONS.includes(fileData.type.toUpperCase())) {
      result.errors.push({
        type: 'validation',
        fileName: fileData.name,
        message: `${fileData.name} is not a valid PSS®E file (${ALLOWED_EXTENSIONS_DISPLAY})`
      });
      return;
    }

    // Check duplicate by absolute path
    if (existingFiles.some(f => f.path === fileData.path)) {
      result.errors.push({
        type: 'duplicate',
        fileName: fileData.name,
        message: `${fileData.name} is already attached`
      });
      return;
    }

    // Copy file to workspace
    try {
      // Check if this filename is already attached to THIS thread (for versioning)
      const sameNameCount = existingFiles.filter(
        f => f.name.toLowerCase() === fileData.name.toLowerCase()
      ).length;

      const copyResult = await (window as any).psse.copyFileToWorkspace(
        fileData.path, threadId, sameNameCount > 0 ? sameNameCount + 1 : 0
      );
      
      if (!copyResult.success) {
        result.errors.push({
          type: 'copy',
          fileName: fileData.name,
          message: `Failed to copy ${fileData.name} to workspace`
        });
        return;
      }

      // Add to result with WORKSPACE path and naming info from Electron
      // Show version suffix (_v2) in UI, but hide thread prefix from user
      let displayName = fileData.name;
      if (copyResult.storedFileName !== copyResult.fileName) {
        // Check if it's a version suffix (e.g. bench_v2.sav) — show it
        const versionMatch = copyResult.storedFileName.match(/_v\d+\.\w+$/);
        if (versionMatch) {
          displayName = copyResult.storedFileName;
        }
        // Otherwise it's a thread prefix — keep original name for display
      }

      const fileAttachment: IFileAttachment = {
        name: displayName,
        path: copyResult.destPath,
        type: fileData.type,
        storedFilename: copyResult.storedFileName,
        fileSize: copyResult.fileSize
      };
      result.addedFiles.push(fileAttachment);
      result.filesAdded++;
      
    } catch (error) {
      console.error(`Error copying file ${fileData.name}:`, error);
      result.errors.push({
        type: 'copy',
        fileName: fileData.name,
        message: `Failed to copy ${fileData.name} to workspace`
      });
    }
  }

  /**
   * Load thread files from backend with smart display name logic
   */
  async loadThreadFiles(threadId: string): Promise<LoadThreadFilesResult> {
    try {
      const response = await this.chatService.getThreadFiles(threadId).toPromise();
      
      if (!response || !response.files) {
        return { files: [] };
      }

      const files = response.files.map((f: any) => {
        // Smart display logic:
        // - "case.sav" → Display: "case.sav" (no prefix/suffix)
        // - "case_v2.sav" → Display: "case_v2.sav" (version suffix - show it)
        // - "threadB_20260211_143025_case.sav" → Display: "case.sav" (hide ugly prefix)
        // - "threadB_20260211_143025_case_v2.sav" → Display: "case_v2.sav" (extract version)
        
        let displayName = f.original_filename || f.filename;
        const storedFilename = f.stored_filename || displayName;
        
        // Check if stored_filename has version suffix (_v2, _v3, etc.)
        const versionMatch = storedFilename.match(/_v(\d+)\./);
        
        if (versionMatch) {
          // Has version suffix - show original name with version
          const namePart = displayName.replace(/\.\w+$/, ''); // Remove extension
          const ext = displayName.split('.').pop(); // Get extension
          displayName = `${namePart}_v${versionMatch[1]}.${ext}`; // case_v2.sav
        } else if (storedFilename !== displayName && !storedFilename.startsWith('_v')) {
          // Has thread prefix but no version - show clean original name
          displayName = f.original_filename || f.filename;
        } else {
          // No prefix or suffix - show stored_filename as-is
          displayName = storedFilename;
        }
        
        return {
          name: displayName,
          path: f.file_path,
          type: f.file_type || f.filename.split('.').pop()?.toUpperCase() || '',
          attachmentId: f.id
        } as IFileAttachment;
      });

      return { files };
    } catch (error) {
      console.error('Error loading thread files:', error);
      return { 
        files: [], 
        error: 'Failed to load thread files'
      };
    }
  }

  /**
   * Remove file - delete from backend if it has an attachmentId,
   * and also remove the physical file from the workspace directory.
   */
  async removeFile(file: IFileAttachment): Promise<RemoveFileResult> {
    const fileName = file.name;

    // If file has an id, delete from backend
    if (file.attachmentId) {
      try {
        await this.chatService.deleteFile(file.attachmentId).toPromise();
      } catch (error) {
        console.error('Error deleting file:', error);
        return {
          success: false,
          fileName,
          error: 'Failed to remove file from thread'
        };
      }
    }

    // Delete physical file from workspace directory
    await this.deletePhysicalFile(file.path);

    return {
      success: true,
      fileName
    };
  }

  /**
   * Delete the physical file from the Electron workspace directory.
   * Silently ignores failures (file may already be gone).
   */
  private async deletePhysicalFile(filePath: string): Promise<void> {
    try {
      const psse = (window as any).psse;
      if (psse?.deleteWorkspaceFile && filePath) {
        const result = await psse.deleteWorkspaceFile(filePath);
        if (!result.success) {
          console.warn('Could not delete workspace file:', result.message);
        }
      }
    } catch (error) {
      console.warn('Error deleting physical workspace file:', error);
    }
  }

  /**
   * Attach files to thread via backend API
   */
  async attachFilesToThread(files: IFileAttachment[], threadId: string): Promise<void> {
    const newFiles = files.filter(f => !f.attachmentId);
    
    if (newFiles.length === 0) {
      return;
    }

    for (const file of newFiles) {
      try {
        const response = await firstValueFrom(
          this.chatService.attachFile(file.path, threadId, file.name, file.storedFilename, file.fileSize)
        );
        
        if (response && response.id) {
          file.attachmentId = response.id;
        }
      } catch (error) {
        console.error(`Error attaching file ${file.name}:`, error);
        throw new Error(`Failed to attach ${file.name}`);
      }
    }
  }

  /**
   * Get success message for file selection
   */
  getFileSelectionSuccessMessage(filesAdded: number): string {
    const plural = filesAdded > 1 ? 'files' : 'file';
    return `${filesAdded} ${plural} attached successfully`;
  }
}
